import { useMutation } from "@apollo/client";
import { z } from "zod";
import { Badge, Body2, Checkbox, FieldError, XStack, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { type BoundFieldMeta, FormPhoneField, phoneValueSchema, useFormEngine, visibleFieldError } from "../../lib/form-engine";
import type { OnboardingIdentity } from "./types";
import { recordPhoneMutation } from "./utils/queries";
export type PhoneOutcomes = {
  submitted: {};
  skipped: {};
};
const Outcome = tagged<PhoneOutcomes>();

// A version identifier, not a consent timestamp.
const TCPA_CONSENT_VERSION = "2026-08-01";
const DISCLOSURE = "I agree to receive automated text messages from Granted Health about my case updates. Msg frequency varies. Msg & data rates may apply. Reply STOP to cancel, HELP for help.";
const phoneNumberSchema = phoneValueSchema();
const phoneSchema = z.object({
  phoneNumber: phoneNumberSchema,
  smsConsent: z.boolean().refine(consented => consented, "Please agree to continue")
});
type PhoneFormValues = z.input<typeof phoneSchema>;
function ConsentCheckbox({
  checked,
  meta,
  onCheckedChange
}: {
  checked: boolean;
  meta: BoundFieldMeta;
  onCheckedChange: (checked: boolean) => void;
}) {
  return <YStack gap="$sp1" data-sentry-element="YStack" data-sentry-component="ConsentCheckbox" data-sentry-source-file="Phone.tsx">
      <XStack gap="$sp1.5" alignItems="flex-start" data-sentry-element="XStack" data-sentry-source-file="Phone.tsx">
        <Checkbox testID="phone.consent" checked={checked} onCheckedChange={value => onCheckedChange(value === true)} aria-label="Agree to receive automated text messages" data-sentry-element="Checkbox" data-sentry-source-file="Phone.tsx" />
        <Body2 color="$textSubtle" flex={1} data-sentry-element="Body2" data-sentry-source-file="Phone.tsx">
          {DISCLOSURE}
        </Body2>
      </XStack>
      <FieldError message={visibleFieldError(meta)} data-sentry-element="FieldError" data-sentry-source-file="Phone.tsx" />
    </YStack>;
}
export function Phone({
  identity,
  onDone
}: {
  identity: OnboardingIdentity;
  onDone: OnDone<PhoneOutcomes>;
}) {
  const [recordPhone] = useMutation(recordPhoneMutation);
  const {
    form,
    formError,
    submit
  } = useFormEngine<PhoneFormValues, z.output<typeof phoneSchema>>({
    defaultValues: {
      phoneNumber: "",
      smsConsent: false
    },
    schema: phoneSchema,
    onSubmit: async values => {
      await recordPhone({
        variables: {
          input: {
            phoneNumber: values.phoneNumber,
            consentVersion: TCPA_CONSENT_VERSION
          }
        }
      });
      return {
        status: "ok"
      };
    },
    onSubmitSuccess: () => onDone(Outcome.submitted())
  });
  return <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-component="Phone" data-sentry-source-file="Phone.tsx">
      <OnboardingScreen.Nav data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Phone.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Phone.tsx">
        <OnboardingScreen.Header alignment="left" title={`Hi, ${identity.firstName} 👋`} subtitle="Stay in the loop — we'll only text you with updates to your case." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Phone.tsx" />
        <YStack gap="$sp1" alignItems="flex-start" data-sentry-element="YStack" data-sentry-source-file="Phone.tsx">
          <Badge status="positive" data-sentry-element="Badge" data-sentry-source-file="Phone.tsx">Recommended</Badge>
          <YStack alignSelf="stretch" data-sentry-element="YStack" data-sentry-source-file="Phone.tsx">
            <form.Field name="phoneNumber" data-sentry-element="form.Field" data-sentry-source-file="Phone.tsx">
              {field => <FormPhoneField field={field} testID="phone.input" placeholder="Enter your phone number" autoComplete="tel" aria-label="Phone number" />}
            </form.Field>
          </YStack>
          <YStack marginTop="$sp1" alignSelf="stretch" data-sentry-element="YStack" data-sentry-source-file="Phone.tsx">
            <form.Field name="smsConsent" data-sentry-element="form.Field" data-sentry-source-file="Phone.tsx">
              {field => <ConsentCheckbox checked={field.state.value} meta={field.state.meta} onCheckedChange={field.handleChange} />}
            </form.Field>
          </YStack>
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Phone.tsx">
        <OnboardingScreen.FormError message={formError} data-sentry-element="OnboardingScreen.FormError" data-sentry-source-file="Phone.tsx" />
        <form.Subscribe selector={state => [state.isSubmitting, state.values.phoneNumber, state.values.smsConsent] as const} data-sentry-element="form.Subscribe" data-sentry-source-file="Phone.tsx">
          {([isSubmitting, phoneNumber, smsConsent]) => <>
              <OnboardingScreen.PrimaryAction testID="phone.submit" variant="brand" disabled={isSubmitting || !smsConsent || !phoneNumberSchema.safeParse(phoneNumber).success} loading={isSubmitting} onPress={() => submit()}>
                Continue
              </OnboardingScreen.PrimaryAction>
              <OnboardingScreen.TertiaryAction testID="phone.skip"
          // Skipping mid-write would still land the number while
          // analytics records a skip.
          disabled={isSubmitting} onPress={() => onDone(Outcome.skipped())}>
                Skip
              </OnboardingScreen.TertiaryAction>
            </>}
        </form.Subscribe>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}